/**
 * Buying a term: the price list, the quote, the order and its settlement.
 *
 * Five endpoints under `/api/v1/billing/prepaid/`, and between them the whole
 * of how Naurist is sold now — a length is picked, priced, paid for once, and
 * runs until it ends. Nothing renews, so there is no mandate here, no cancel
 * and no refund; `subscriptionsService` beside this one still types all three
 * for the workspaces that signed up under the old flow, and nothing on the
 * screens reads it any more.
 *
 * The rules this file keeps, all four of them for the same reason — that the
 * figure in front of the Buy button has to be the figure an invoice is raised
 * for:
 *
 *   - **No price is worked out here.** Not the tax, not the credit, not the
 *     discount. `fetchPrepaidQuote` asks and the server answers; this file
 *     turns decimal strings into numbers and nothing else.
 *   - **`purchase_kind` comes off the quote.** What a purchase *is* — a
 *     renewal, an upgrade, a conversion — is the server's reading, and the
 *     one place this file makes that judgement itself is `availabilityOf`,
 *     which decides what to *show* and never what to charge.
 *   - **`expected_total` is the quote's own figure**, printed back to the
 *     paisa. It is a staleness check: a credit counted in whole IST days drops
 *     at midnight there, and this is what stops a price from an hour ago
 *     becoming an invoice.
 *   - **Settlement is read, never assumed.** Creating an order grants nothing.
 *     A term exists when the backend says it does, which is what
 *     `pollPrepaidOrder` is for and why a browser closing mid-payment loses
 *     nothing at all.
 *
 * Money crosses as a decimal string — `"8016.39"` — and is a number from here
 * on. The one exception travels untouched: `checkout.amount` is integer paise,
 * because it is what the gateway signs against.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import { ApiError } from "../../../lib/http";
import type { GetToken } from "../../auth/types/auth.types";
import { PRO_FEATURES, recommendedOf } from "../data/subscriptionPlans";
import { monthsLabel } from "../pricing";
import type { BillingCycleId, SubscriptionPlan } from "../types/billing.types";
import {
  PREPAID_CYCLES,
  type PrepaidCheckoutResponse,
  type PrepaidOrder,
  type PrepaidOrderRequest,
  type PrepaidOrderResponse,
  type PrepaidOrderStatus,
  type PrepaidPlanAvailability,
  type PrepaidPlanResponse,
  type PrepaidPrice,
  type PrepaidQuote,
  type PrepaidQuoteRequest,
  type PrepaidQuoteResponse,
  type PrepaidSettlement,
  type PrepaidTerm,
  type PrepaidTermResponse,
  type PrepaidTerms,
  type PrepaidTermsResponse,
  type PrepaidTermStatus,
  type PurchaseKind,
  type TermBillingType,
  type TermCredit,
  type VerifyPrepaidOrderRequest,
} from "../types/prepaid.types";

/** Money, to the paisa — the same rounding the rest of the feature does. */
const round2 = (amount: number): number => Math.round(amount * 100) / 100;

/** Decimal string to number, defended: an absent or unparseable figure lands
 *  on 0 rather than painting "₹ NaN" onto a price. Local, as it is in every
 *  other service here. */
const toAmount = (raw: string | null | undefined): number => {
  const value = Number.parseFloat(raw ?? "");
  return Number.isFinite(value) ? value : 0;
};

/**
 * A figure back out as the backend writes money: two decimals, always.
 *
 * What `expected_total` goes up as. Printed from the quote's own number rather
 * than carried as the string it arrived on, which comes to the same decimal and
 * saves threading a raw copy of one field through every shape in between —
 * `708` becomes `"708.00"`, `8016.39` stays itself.
 */
const toDecimalString = (amount: number): string => amount.toFixed(2);

/** The purchase kinds, as a set to narrow an unknown string against. */
const PURCHASE_KINDS: PurchaseKind[] = [
  "NEW",
  "RENEWAL",
  "UPGRADE",
  "DOWNGRADE",
  "INTERNAL_CONVERSION",
];

/**
 * The server's word for what a purchase is, narrowed — or `NEW` for a word
 * this build has never heard of.
 *
 * `NEW` is the safe landing rather than a guess: it is the kind that credits
 * nothing, queues nothing and replaces nothing, so a purchase read wrongly as
 * one is a purchase shown at its own full price, which is the error that costs
 * nobody anything.
 */
const toPurchaseKind = (raw: string | null | undefined): PurchaseKind =>
  PURCHASE_KINDS.find((kind) => kind === (raw ?? "").toUpperCase()) ?? "NEW";

const ORDER_STATUSES: PrepaidOrderStatus[] = [
  "CREATED",
  "PAID",
  "SUPERSEDED",
  "EXPIRED",
  "FAILED",
];

/**
 * An order's status, narrowed — or `FAILED` for one this build cannot place.
 *
 * The opposite instinct to `toPurchaseKind` above, and deliberately: an
 * unknown status is read as the state that stops the screen waiting and sends
 * the reader to support, rather than as `CREATED`, which would poll a dead
 * order for a minute and then say nothing.
 */
const toOrderStatus = (raw: string | null | undefined): PrepaidOrderStatus =>
  ORDER_STATUSES.find((status) => status === (raw ?? "").toUpperCase()) ??
  "FAILED";

const toTermStatus = (raw: string | null | undefined): PrepaidTermStatus => {
  const word = (raw ?? "").toUpperCase();
  if (word === "SCHEDULED") return "SCHEDULED";
  if (word === "EXPIRED") return "EXPIRED";
  return "ACTIVE";
};

const toBillingType = (raw: string | null | undefined): TermBillingType =>
  (raw ?? "").toUpperCase() === "ONE_TIME" ? "ONE_TIME" : "PREPAID";

/**
 * A coupon code off a response, or `null`.
 *
 * The backend answers `""` for a purchase with no coupon on it, which is a
 * value a screen would happily draw as an empty discount line. One absent
 * thing, one spelling.
 */
const toCoupon = (raw: string | null | undefined): string | null => {
  const code = (raw ?? "").trim();
  return code ? code : null;
};

/* ── Plans ──────────────────────────────────────────────────────────────── */

/**
 * One row of the prepaid price list as a duration row this app can already
 * draw, or `null` for a length it has no row for.
 *
 * The same `SubscriptionPlan` the recurring list produced, on purpose: the
 * cards, the duration rows and the checkout summary are the same design, and
 * the only thing that actually changed underneath them is which endpoint says
 * what a term costs. Two app shapes for one set of cards would be two shapes to
 * keep in step.
 *
 * ## The struck-through figure
 *
 * The duration rows print two prices: what the term costs, and a struck one
 * beside it. On the recurring list that second figure was `list_amount` — what
 * the plan was listed at before its discount.
 *
 * `compare_at_amount` is the prepaid row's version of it, and what this reads:
 * a genuine former price, pre-tax like `amount` beside it, so ₹1,800 is struck
 * through at ₹2,400 — the same term cheaper, which is what the design's own
 * figures (₹800 against ₹600) always meant.
 *
 * It replaced `gross_amount` here, which the row drew between 2026-10-10 and
 * 2026-10-14 because nothing else on the row carried a former price. That was
 * `amount` with 18% GST on it — the same term taxed, not the same term
 * cheaper — and it is **not** what gets struck. `gross_amount` stays on the
 * wire shape for what it is: the taxed list price, quoted nowhere a purchase
 * is being priced.
 *
 * A term with no discount sends `compare_at_amount` equal to `amount`, or no
 * `compare_at_amount` at all, and then nothing is struck rather than a figure
 * being struck at its own price.
 *
 * Two fields still have no source and are left empty rather than invented:
 *
 *   - `saving` is `null`. It is the "Save ₹X over a year" badge, which nothing
 *     draws today; the duration row works its own "normally ₹X" out of the two
 *     prices it already has. The lengths still compare on `pricePerMonth`,
 *     which is what the cards read anyway.
 *   - `totalCycles` is `null`: a term is bought once and holds its price for
 *     exactly the months it covers, so "how many cycles does this price hold
 *     for" has no answer rather than the answer `1`.
 */
export const toPrepaidPlan = (
  row: PrepaidPlanResponse,
  recommended: BillingCycleId | null = null
): SubscriptionPlan | null => {
  const months = row.duration_months;
  const cycle = PREPAID_CYCLES[months];
  if (!cycle) return null;

  const cyclePrice = toAmount(row.amount);
  const perMonth = toAmount(row.monthly_equivalent);
  // What the term was listed at before its discount, which is what the row
  // strikes through. Only above the price counts: equal to it, missing, or
  // somehow below it are all "this term is not discounted".
  const compareAt = toAmount(row.compare_at_amount);
  const listPrice = compareAt > cyclePrice ? compareAt : 0;

  return {
    id: cycle,
    months,
    code: row.code,
    productName: row.name,
    cycleLabel: monthsLabel(months),
    pricePerMonth: perMonth,
    cyclePrice,
    // Divided down to a month, so the two figures the card puts side by side
    // are of the same thing. Falls back to the term's own price where the row
    // listed no former one, which draws no struck price at all rather than
    // striking a zero through.
    listPricePerMonth: listPrice > 0 ? round2(listPrice / months) : perMonth,
    listCyclePrice: listPrice > 0 ? listPrice : cyclePrice,
    saving: null,
    recommended: cycle === recommended,
    totalCycles: null,
    // Ours, not the endpoint's: what the product includes is copy this app
    // writes, and it is the same product on all four lengths.
    features: PRO_FEATURES,
  };
};

/**
 * Every term on sale, shortest first.
 *
 * Ordered here rather than trusted from the wire, and the badge decided over
 * the whole list rather than per row — both for the reasons `fetchPlans` gives,
 * and through the same `recommendedOf`, so the two price lists cannot disagree
 * about where the badge goes.
 *
 * The one endpoint in this file any member may read. A staff account gets the
 * price list and `403 billing_owner_required` from everything else, which is
 * why this is the call the screen probes with.
 */
export async function fetchPrepaidPlans(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<SubscriptionPlan[]> {
  const rows = await api.get<PrepaidPlanResponse[] | null>(
    API_ENDPOINTS.billing.prepaid.plans,
    { token: await getToken(), signal }
  );

  const drawable = (rows ?? [])
    .map((row) => toPrepaidPlan(row))
    .filter((plan): plan is SubscriptionPlan => plan !== null);

  const recommended = recommendedOf(drawable.map((plan) => plan.id));

  return drawable
    .map((plan) => ({ ...plan, recommended: plan.id === recommended }))
    .sort((a, b) => a.months - b.months);
}

/* ── Terms ──────────────────────────────────────────────────────────────── */

export const toPrepaidTerm = (
  response: PrepaidTermResponse | null | undefined
): PrepaidTerm | null =>
  response
    ? {
        planCode: response.plan_code,
        billingType: toBillingType(response.billing_type),
        status: toTermStatus(response.status),
        startsOn: response.period_start,
        endsOn: response.period_end,
      }
    : null;

/**
 * The term running now and the one queued behind it.
 *
 * What "your plan" and "your next plan" are drawn from, and the only call that
 * can answer the second. Note what this is *not*: it is not the access
 * decision. Whether the product is open is `access_source` on
 * `GET /billing/subscription/`, which counts a scheduled term whose day has
 * come even before the backend's sweep has promoted it — so on the first
 * morning of a renewal these dates can still be yesterday's while access is
 * already right. See `fetchTrialAccess`.
 */
export async function fetchPrepaidTerms(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<PrepaidTerms> {
  const response = await api.get<PrepaidTermsResponse | null>(
    API_ENDPOINTS.billing.prepaid.terms,
    { token: await getToken(), signal }
  );

  return {
    current: toPrepaidTerm(response?.current),
    queued: toPrepaidTerm(response?.queued),
  };
}

/* ── Price, quote, order ────────────────────────────────────────────────── */

/**
 * The money off a quote or an order — one mapping, because the two carry the
 * same eleven figures and an order is only a quote that has stopped moving.
 *
 * `months` is passed in rather than read: neither response says how long the
 * term is, and the only honest source for it is the price list the plan came
 * from. A term whose length is not known to this build comes through as 0,
 * which the summary draws as a term it names by its dates instead.
 */
const toPrepaidPrice = (
  response: PrepaidQuoteResponse | PrepaidOrderResponse,
  months: number
): PrepaidPrice => {
  const cgst = toAmount(response.cgst_amount);
  const sgst = toAmount(response.sgst_amount);
  const igst = toAmount(response.igst_amount);

  return {
    months,
    base: toAmount(response.base_amount),
    discount: toAmount(response.discount_amount),
    credit: toAmount(response.credit_amount),
    taxable: toAmount(response.taxable_amount),
    cgst,
    sgst,
    igst,
    tax: round2(cgst + sgst + igst),
    // Kept as the server printed it. A quote says "18" and an order "18.00";
    // they are the same rate, and nothing here compares them.
    taxRateLabel: response.tax_rate_percent,
    total: toAmount(response.total_amount),
    amountDue: toAmount(response.amount_due),
  };
};

const toTermCredit = (
  response: PrepaidQuoteResponse["credit"]
): TermCredit | null =>
  response
    ? {
        amount: toAmount(response.amount),
        basisAmount: toAmount(response.basis_amount),
        unusedDays: response.unused_days,
        termDays: response.term_days,
      }
    : null;

export const toPrepaidQuote = (
  response: PrepaidQuoteResponse,
  months: number
): PrepaidQuote => ({
  purchaseKind: toPurchaseKind(response.purchase_kind),
  planCode: response.plan_code,
  startsOn: response.starts_on,
  endsOn: response.ends_on,
  currentTerm: toPrepaidTerm(response.current_term),
  credit: toTermCredit(response.credit),
  coupon: toCoupon(response.coupon_code),
  price: toPrepaidPrice(response, months),
});

/**
 * What a purchase would cost today, and what it would buy.
 *
 * Records nothing — no row, no held coupon, no gateway call — so it is asked
 * whenever the plan or the coupon changes, and **asked again immediately
 * before the order is created**. That second ask is not belt and braces: the
 * credit for a term being cut short is counted in whole IST days, so it drops
 * at midnight there, and an order carrying last night's figure is refused.
 *
 * `months` is the length of the plan being quoted, off the price list. It is
 * threaded through because neither this response nor the order's says how long
 * a term runs, and the alternative is a second lookup at every call site that
 * wants to print "3 months".
 *
 * Every refusal arrives as an `ApiError` with the backend's `code` on it —
 * `prepaid_term_already_queued` while a future term is waiting,
 * `legacy_subscription_cleanup_required` where only support can help, the
 * coupon errors, the two floors that stop an invoice being raised for less
 * than a rupee. Read it with `prepaidErrorCode` and write copy per code; the
 * server's `message` is display text and its `code` is the contract.
 */
export async function fetchPrepaidQuote(
  getToken: GetToken,
  ask: { planCode: string; couponCode?: string | null; months: number },
  signal?: AbortSignal
): Promise<PrepaidQuote> {
  // Trimmed and upper-cased, which is the one thing this side does to a code,
  // and the same thing `fetchQuote` does to it: a coupon typed in lower case
  // with a space on the end is the same coupon. Whether it holds is the
  // server's answer and nothing is checked here first.
  const coupon = ask.couponCode?.trim().toUpperCase();
  const body: PrepaidQuoteRequest = {
    plan_code: ask.planCode,
    // Sent only where there is one, so the shape of the request matches the
    // purchase that follows it.
    ...(coupon ? { coupon_code: coupon } : {}),
  };

  const response = await api.post<PrepaidQuoteResponse>(
    API_ENDPOINTS.billing.prepaid.quote,
    body,
    { token: await getToken(), signal }
  );

  return toPrepaidQuote(response, ask.months);
}

const toPrepaidCheckout = (
  response: PrepaidCheckoutResponse | null | undefined
): PrepaidOrder["checkout"] =>
  response
    ? {
        keyId: response.key_id,
        orderId: response.order_id,
        // Untouched. This is what the gateway signs against; a conversion of
        // our own between paise and rupees would be a mismatch nobody could
        // explain afterwards.
        amountInPaise: response.amount,
        currency: response.currency,
        name: response.name,
        description: response.description,
        prefill: {
          name: response.prefill?.name ?? "",
          email: response.prefill?.email ?? "",
          contact: response.prefill?.contact ?? "",
        },
        notes: response.notes ?? {},
        timeoutSeconds: response.timeout,
      }
    : null;

export const toPrepaidOrder = (
  response: PrepaidOrderResponse,
  months: number
): PrepaidOrder => ({
  id: response.public_id,
  status: toOrderStatus(response.status),
  purchaseKind: toPurchaseKind(response.purchase_kind),
  planCode: response.plan_code,
  startsOn: response.starts_on,
  endsOn: response.ends_on,
  expiresAt: response.expires_at,
  coupon: toCoupon(response.coupon_code),
  price: toPrepaidPrice(response, months),
  term: response.term
    ? {
        startsOn: response.term.period_start,
        endsOn: response.term.period_end,
      }
    : null,
  checkout: toPrepaidCheckout(response.checkout),
});

/**
 * Freezing an accepted quote and opening a gateway order against it.
 *
 * Takes the quote rather than a plan and a figure, which is the point: the
 * `expected_total` that goes up is the `amountDue` the reader was actually
 * shown, and a call site that could pass one without the other is a call site
 * that could check a price nobody saw.
 *
 * **Nothing is granted here.** No term, no invoice, no access — those come from
 * settlement. What comes back is the order, and its `checkout` is what the
 * gateway's window is opened with.
 *
 * Asking twice for the same purchase answers the same order again, so a double
 * click or a reload is safe and an abandoned checkout is reopened by simply
 * asking again. A *different* purchase supersedes the open one. The refusals
 * worth copy of their own are `prepaid_quote_changed`, which carries the
 * current figures in `details` and means quote again and re-confirm;
 * `billing_profile_incomplete`, which names the missing fields and is the one
 * refusal with somewhere to send the reader; and `prepaid_order_in_progress`,
 * which is two creates racing and answers the open order if asked once more.
 */
export async function createPrepaidOrder(
  getToken: GetToken,
  quote: PrepaidQuote,
  signal?: AbortSignal
): Promise<PrepaidOrder> {
  const body: PrepaidOrderRequest = {
    plan_code: quote.planCode,
    ...(quote.coupon ? { coupon_code: quote.coupon } : {}),
    expected_total: toDecimalString(quote.price.amountDue),
  };

  const response = await api.post<PrepaidOrderResponse>(
    API_ENDPOINTS.billing.prepaid.orders,
    body,
    { token: await getToken(), signal }
  );

  return toPrepaidOrder(response, quote.price.months);
}

/**
 * One order, by the id the create answered with.
 *
 * What the screen reads on its way back to a checkout it left — on a reload,
 * on a return from the gateway's window, on a session picked up on another
 * device. A pending payment is a thing to find out about rather than to assume
 * the worst of.
 */
export async function fetchPrepaidOrder(
  getToken: GetToken,
  orderId: string,
  months = 0,
  signal?: AbortSignal
): Promise<PrepaidOrder> {
  const response = await api.get<PrepaidOrderResponse>(
    API_ENDPOINTS.billing.prepaid.order(orderId),
    { token: await getToken(), signal }
  );

  return toPrepaidOrder(response, months);
}

/**
 * Handing the gateway's success callback to the backend, which proves it
 * against its own secret, reads the payment from the gateway itself, and
 * settles the order.
 *
 * The three fields go up exactly as the window handed them over. Called from
 * the success handler and from nowhere else: a declined card is the gateway's
 * own window to offer again, and this has nothing to say about one.
 *
 * Two successes, not one, and the screen has to tell them apart —
 * `settled: false` is a payment only authorised so far, which is neither a
 * failure nor something to retry. The order is polled from there. `settled` is
 * read off the order's own status rather than off the HTTP code, because they
 * say the same thing and only one of them survives the client.
 *
 * Idempotent, and safely raced against the webhook: verifying the same payment
 * twice answers the same settled order and writes nothing new.
 *
 * The one refusal to read carefully is `prepaid_payment_needs_review`: the
 * money was taken, the term could not be placed, the order is `FAILED`, and an
 * alert is already raised for somebody to handle it by hand. **Never retry the
 * purchase on it** — that is how a practice pays twice.
 */
export async function verifyPrepaidOrder(
  getToken: GetToken,
  orderId: string,
  receipt: VerifyPrepaidOrderRequest,
  months = 0,
  signal?: AbortSignal
): Promise<PrepaidSettlement> {
  const response = await api.post<PrepaidOrderResponse>(
    API_ENDPOINTS.billing.prepaid.verifyOrder(orderId),
    receipt,
    { token: await getToken(), signal }
  );

  const order = toPrepaidOrder(response, months);
  return { settled: order.status === "PAID", order };
}

/* ── Settlement ─────────────────────────────────────────────────────────── */

/** The statuses there is no point asking about twice. `CREATED` is the only
 *  one still in flight; the rest are either done or somebody else's to sort
 *  out. */
const SETTLED_STATUSES: PrepaidOrderStatus[] = [
  "PAID",
  "SUPERSEDED",
  "EXPIRED",
  "FAILED",
];

/** Whether an order has stopped moving. */
export const isSettled = (order: PrepaidOrder): boolean =>
  SETTLED_STATUSES.includes(order.status);

/** How long to keep asking, and how often. The webhook usually settles within
 *  seconds; the backend's sweep is the backstop at ten minutes, which is far
 *  longer than anybody will watch a spinner, so this gives up and leaves the
 *  order to be read again on the next visit. */
const POLL_INTERVAL_MS = 3000;
const POLL_ATTEMPTS = 20;

const wait = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(signal.reason ?? new Error("aborted"));
      },
      { once: true }
    );
  });

/**
 * Waiting for a payment to settle: the order, asked for until it stops moving.
 *
 * Needed because settlement is not the browser's to do. A payment is settled by
 * the gateway's webhook, or by the backend's sweep, or by the verify call — and
 * whichever gets there first, the answer appears on this record. A browser that
 * closed halfway has not lost a purchase; it has only stopped watching.
 *
 * Gives up after about a minute and hands back whatever the order said last,
 * still `CREATED`. That is not a failure either: the copy for it is that the
 * payment is being confirmed, and the next read of the order will say so. A
 * screen that turned a slow webhook into "payment failed" would be telling
 * somebody who has paid that they have not.
 */
export async function pollPrepaidOrder(
  getToken: GetToken,
  orderId: string,
  options: {
    months?: number;
    /** Called with each answer, so the screen can show the status moving. */
    onUpdate?: (order: PrepaidOrder) => void;
    signal?: AbortSignal;
  } = {}
): Promise<PrepaidOrder> {
  const { months = 0, onUpdate, signal } = options;
  let latest = await fetchPrepaidOrder(getToken, orderId, months, signal);
  onUpdate?.(latest);

  for (let attempt = 1; attempt < POLL_ATTEMPTS; attempt += 1) {
    if (isSettled(latest)) return latest;
    await wait(POLL_INTERVAL_MS, signal);
    latest = await fetchPrepaidOrder(getToken, orderId, months, signal);
    onUpdate?.(latest);
  }

  return latest;
}

/* ── Reading the backend's refusals ─────────────────────────────────────── */

/**
 * The backend's `code` off a failure, or `null` for one that carried none.
 *
 * **The code is the contract and the message is display text**, which is the
 * rule the whole of prepaid billing is written to: branch on this, never on
 * `error.message`, which is copy the backend may reword.
 */
export const prepaidErrorCode = (error: unknown): string | null => {
  if (!(error instanceof ApiError)) return null;
  const body = error.data;
  if (!body || typeof body !== "object") return null;

  const code = (body as { code?: unknown }).code;
  return typeof code === "string" && code ? code : null;
};

/**
 * The `details` off a failure, which a few codes carry and the screen needs:
 * the current figures on `prepaid_quote_changed`, the missing fields on
 * `billing_profile_incomplete`, the kind on `prepaid_purchase_not_available`.
 */
export const prepaidErrorDetails = (
  error: unknown
): Record<string, unknown> | null => {
  if (!(error instanceof ApiError)) return null;
  const body = error.data;
  if (!body || typeof body !== "object") return null;

  const details = (body as { details?: unknown }).details;
  return details && typeof details === "object"
    ? (details as Record<string, unknown>)
    : null;
};

/**
 * Whether prepaid billing is switched off on this backend.
 *
 * `404 prepaid_billing_unavailable` from any of these paths, and the price list
 * is where it is probed because it is the one call a non-owner can make. There
 * is no endpoint that reports the flag, so this is the whole of the signal.
 *
 * It is not a state with a second flow behind it any more: buying a term is how
 * Naurist is sold, so the Billing screen draws this as the failure it is rather
 * than quietly offering the mandate flow instead.
 */
export const isPrepaidUnavailable = (error: unknown): boolean =>
  prepaidErrorCode(error) === "prepaid_billing_unavailable";

/* ── What may be bought today ───────────────────────────────────────────── */

/** The two kinds that credit an unused term, and so the two the backend's
 *  credit flag gates. */
const CREDIT_KINDS: PurchaseKind[] = ["UPGRADE", "INTERNAL_CONVERSION"];

export const isCreditKind = (kind: PurchaseKind): boolean =>
  CREDIT_KINDS.includes(kind);

/**
 * What buying a given length would be, judged from the term that is running.
 *
 * The one place in this file that makes the server's judgement itself, and it
 * is here under protest. The backend classifies a purchase by `(tier_rank,
 * months)` against the current term and answers `purchase_kind` on the quote —
 * but a quote is one call per plan, every quote is refused outright while a
 * term is queued, and the flag that decides whether a credited purchase can be
 * *bought* is reported by no endpoint at all. So a price list that only learned
 * an upgrade was unavailable from the 409 at the Buy button would show four
 * cards and refuse two of them.
 *
 * This decides what is **shown**. It never decides a price, and the kind on a
 * quote always wins over the kind guessed here — see `fetchPrepaidQuote`, which
 * is still the only thing that may name a figure. If the backend ever exposes
 * the flag, or prices a second tier, this goes.
 */
export const purchaseKindFor = (
  months: number,
  current: { months: number | null; planCode: string; oneTime: boolean } | null
): PurchaseKind => {
  if (!current) return "NEW";
  // An Internal Billing term is bought out of, whatever length is picked.
  if (current.oneTime) return "INTERNAL_CONVERSION";
  // A length this build cannot place the current term at: say `NEW`, which
  // credits and queues nothing, and let the quote correct it.
  if (current.months === null) return "NEW";
  if (months === current.months) return "RENEWAL";
  return months > current.months ? "UPGRADE" : "DOWNGRADE";
};

/**
 * Which of the plans on sale may actually be bought right now, and what each
 * one would be.
 *
 * Two things make a card unsellable, and both are the product's rules rather
 * than failures:
 *
 *   - **A term already queued.** One future term at a time, so while a renewal
 *     or a downgrade is waiting, every quote is refused —  upgrades included.
 *     Nothing is sellable until it starts, and the screen says why.
 *   - **A credited purchase.** An upgrade or a conversion quotes happily and is
 *     refused at the order while the backend's credit flag is off, with no way
 *     to ask which it is. Hidden rather than offered and then refused, so
 *     nobody meets a dead end at a Buy button.
 *
 * Keyed by plan code, which is what the cards carry.
 */
export const availabilityOf = (
  plans: SubscriptionPlan[],
  terms: PrepaidTerms | null
): Record<string, PrepaidPlanAvailability> => {
  const current = terms?.current ?? null;
  const monthsOfCode = new Map(plans.map((plan) => [plan.code, plan.months]));
  const held = current
    ? {
        months: monthsOfCode.get(current.planCode) ?? null,
        planCode: current.planCode,
        oneTime: current.billingType === "ONE_TIME",
      }
    : null;

  const queued = Boolean(terms?.queued);

  return plans.reduce<Record<string, PrepaidPlanAvailability>>((map, plan) => {
    const kind = purchaseKindFor(plan.months, held);
    map[plan.code] = {
      sellable: !queued && !isCreditKind(kind),
      kind,
    };
    return map;
  }, {});
};
